import { useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db/db';
import { useAthleteStore } from '@/store';
import { Plus, User, Shield, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui';

export function ProfileSelectPage() {
    const navigate = useNavigate();
    const { setActiveId, activeId } = useAthleteStore();

    const athletes = useLiveQuery(() => db.athletes.toArray()) ?? [];

    const handleSelect = async (id: string) => {
        await setActiveId(id);
        navigate('/');
    };

    return (
        <div className="min-h-screen bg-bg flex flex-col items-center justify-center p-6 animate-fade-in">
            <div className="w-full max-w-md space-y-8">
                <div className="text-center space-y-2">
                    <div className="w-16 h-16 bg-gold-base/10 rounded-2xl flex items-center justify-center mx-auto mb-6 text-gold-base">
                        <Shield className="w-8 h-8" />
                    </div>
                    <h1 className="text-3xl font-black text-white tracking-tight">Who is wrestling?</h1>
                    <p className="text-text-muted">Select your profile to load your stats and drills.</p>
                </div>

                <div className="grid gap-4">
                    {athletes.map(athlete => {
                        const isActive = athlete.id === activeId;
                        return (
                            <button
                                key={athlete.id}
                                onClick={() => handleSelect(athlete.id)}
                                className={cn(
                                    "group relative flex items-center gap-4 p-4 rounded-2xl transition-all duration-300 border text-left",
                                    isActive
                                        ? "bg-bg-elevated border-gold-base/50 shadow-[0_0_20px_rgba(212,175,55,0.1)]"
                                        : "bg-bg-surface border-white/5 hover:border-white/10 hover:bg-bg-elevated"
                                )}
                            >
                                <div className="w-12 h-12 rounded-full bg-bg-surface border border-white/10 flex items-center justify-center text-2xl shadow-inner relative overflow-hidden group-hover:scale-105 transition-transform">
                                    {athlete.avatar || <User className="w-6 h-6 text-text-muted" />}
                                    {isActive && <div className="absolute inset-0 bg-gold-base/10" />}
                                </div>

                                <div className="flex-1">
                                    <h3 className={cn("font-bold text-lg", isActive ? "text-gold-base" : "text-white")}>
                                        {athlete.name}
                                    </h3>
                                    <p className="text-xs text-text-muted font-medium uppercase tracking-wider">
                                        {athlete.level} • {athlete.experienceYears}y Exp
                                    </p>
                                </div>

                                <ChevronRight className={cn(
                                    "w-5 h-5 transition-transform group-hover:translate-x-1",
                                    isActive ? "text-gold-base" : "text-white/20"
                                )} />
                            </button>
                        );
                    })}

                    <button
                        onClick={() => navigate('/profiles/create')}
                        className="flex items-center gap-4 p-4 rounded-2xl border border-dashed border-white/10 hover:border-white/20 hover:bg-white/5 transition-all group text-left"
                    >
                        <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center text-text-muted group-hover:text-white transition-colors">
                            <Plus className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="font-bold text-text-secondary group-hover:text-white transition-colors">Add New Profile</h3>
                            <p className="text-xs text-text-muted">Create a new wrestler account</p>
                        </div>
                    </button>
                </div>

                <div className="text-center pt-8">
                    <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
                        Continue as Guest
                    </Button>
                </div>
            </div>
        </div>
    );
}
